import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LucideArrowLeft, LucideChevronRight } from '@lucide/angular';
import { Subscription, forkJoin, of } from 'rxjs';
import { describeGenericApiError } from '../../../core/http/api-error.util';
import { ActivityTypeService } from '../../activities/activity-type.service';
import { ActivityType } from '../../activities/activity-type.model';
import { HabitIncrementResponse, HabitTargetResponse } from '../../habits/habit.model';
import { HabitService } from '../../habits/habit.service';
import { CreateTaskRequest, Task } from '../../tasks/task.model';
import { TaskService } from '../../tasks/task.service';
import { nextIdAfter, orderedHabitActivityTypeIds } from '../habit-sort.util';
import { DayStore } from '../day.store';
import {
  MONTH_DAYS,
  formatRecurrenceDays,
  initialRecurrenceDays,
  recurrenceDaysError
} from '../task-recurrence.util';

@Component({
  selector: 'app-habit-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    LucideArrowLeft,
    LucideChevronRight
  ],
  templateUrl: './habit-detail.component.html',
  styleUrls: ['./habit-detail.component.scss']
})
export class HabitDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dayStore = inject(DayStore);
  private readonly activityTypeService = inject(ActivityTypeService);
  private readonly habitService = inject(HabitService);
  private readonly taskService = inject(TaskService);

  private routeParamSubscription: Subscription | null = null;

  readonly activityTypeId = signal<string>(this.route.snapshot.paramMap.get('activityTypeId') ?? '');
  readonly activityTypes = signal<ActivityType[]>([]);
  readonly peerHabitIds = signal<string[]>([]);
  readonly day = this.dayStore.day;
  readonly tasks = signal<Task[]>([]);
  readonly completedTasks = signal<Task[]>([]);

  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly taskError = signal<string | null>(null);
  readonly targetSaveError = signal<string | null>(null);
  readonly incrementError = signal<string | null>(null);

  readonly taskCreating = signal(false);
  readonly taskReorderSubmitting = signal(false);
  readonly taskCompletingIds = signal<Record<string, boolean>>({});
  readonly taskReopeningIds = signal<Record<string, boolean>>({});
  readonly taskDeletingIds = signal<Record<string, boolean>>({});
  readonly targetSaving = signal(false);
  readonly incrementing = signal(false);

  readonly taskRows = computed(() => this.sortByDisplayOrder(this.tasks()));
  readonly currentHabit = computed(() => this.activityTypes().find((activity) => activity.id === this.activityTypeId()) ?? null);
  readonly currentDayHabit = computed(() => this.day()?.habits.find((habit) => habit.activityTypeId === this.activityTypeId()) ?? null);
  readonly nextHabitId = computed(() => nextIdAfter(this.activityTypeId(), this.peerHabitIds()));
  readonly openTaskCount = computed(() => this.taskRows().length);

  newTaskTitle = '';
  readonly newTaskRecurrenceType = signal<'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'>('NONE');
  readonly newTaskRecurrenceDays = signal<number[]>([]);
  readonly monthDays = MONTH_DAYS;
  readonly targetDraft = signal<number | null>(null);

  ngOnInit(): void {
    this.load();
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      const nextId = params.get('activityTypeId') ?? '';
      if (nextId === this.activityTypeId()) {
        return;
      }
      this.activityTypeId.set(nextId);
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.routeParamSubscription?.unsubscribe();
  }

  goBack(): void {
    this.router.navigate(['/today'], {
      queryParams: { tab: 'habits' }
    });
  }

  navigateToNext(): void {
    const nextHabitId = this.nextHabitId();
    if (!nextHabitId) {
      return;
    }
    this.router.navigate(['/today/habit', nextHabitId], {
      queryParams: { from: 'habits' }
    });
  }

  openTask(taskId: string): void {
    this.router.navigate(['/today/habit', this.activityTypeId(), 'task', taskId], {
      queryParams: { from: 'habits' }
    });
  }

  getTargetDraftInput(): string | number {
    const value = this.targetDraft();
    if (value === null || value === undefined) {
      return '';
    }
    return value;
  }

  setTargetDraftInput(value: string | number | null): void {
    if (value === '' || value === null) {
      this.targetDraft.set(null);
      return;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.targetDraft.set(parsed);
  }

  saveTarget(): void {
    const currentDay = this.day();
    if (!currentDay || this.targetSaving()) {
      return;
    }
    const draft = this.targetDraft();
    if (draft !== null && (!Number.isInteger(draft) || draft < 1)) {
      this.targetSaveError.set('Daily target must be a whole number of at least 1, or empty to clear.');
      return;
    }
    this.targetSaving.set(true);
    this.targetSaveError.set(null);
    this.habitService.setTarget(this.activityTypeId(), {
      dayDate: currentDay.dayDate,
      target: draft
    }).subscribe({
      next: (response) => {
        this.applyHabitTarget(response);
        this.targetSaving.set(false);
      },
      error: (err: unknown) => {
        this.targetSaveError.set(describeGenericApiError(err, 'Failed to save daily target'));
        this.targetSaving.set(false);
      }
    });
  }

  incrementHabit(): void {
    const currentDay = this.day();
    if (!currentDay || this.incrementing()) {
      return;
    }
    this.incrementError.set(null);
    this.incrementing.set(true);
    this.habitService.increment(this.activityTypeId(), {
      dayDate: currentDay.dayDate,
      increment: 1
    }).subscribe({
      next: (response) => {
        this.applyHabitIncrement(response);
        this.incrementing.set(false);
      },
      error: (err: unknown) => {
        this.incrementError.set(describeGenericApiError(err, 'Failed to increment habit'));
        this.incrementing.set(false);
      }
    });
  }

  createTask(): void {
    const title = this.newTaskTitle.trim();
    if (!title || this.taskCreating()) {
      return;
    }
    const daysError = recurrenceDaysError(this.newTaskRecurrenceType(), this.newTaskRecurrenceDays());
    if (daysError) {
      this.taskError.set(daysError);
      return;
    }
    this.taskError.set(null);
    this.taskCreating.set(true);
    this.taskService.create(this.buildCreateTaskRequest(title)).subscribe({
      next: (task) => {
        this.tasks.update((current) => [...current, task]);
        this.newTaskTitle = '';
        this.newTaskRecurrenceType.set('NONE');
        this.newTaskRecurrenceDays.set([]);
        this.taskCreating.set(false);
      },
      error: (err: unknown) => {
        this.taskError.set(describeGenericApiError(err, 'Failed to create task'));
        this.taskCreating.set(false);
      }
    });
  }

  completeTask(task: Task): void {
    if (this.taskCompletingIds()[task.id]) {
      return;
    }
    const previous = this.tasks();
    const previousCompleted = this.completedTasks();
    this.tasks.set(previous.filter((current) => current.id !== task.id));
    this.completedTasks.set([{ ...task, completedAt: new Date().toISOString() }, ...previousCompleted]);
    this.taskCompletingIds.update((current) => ({ ...current, [task.id]: true }));
    this.taskService.complete(task.id).subscribe({
      next: (completedTask) => {
        this.completedTasks.update((current) => [completedTask, ...current.filter((entry) => entry.id !== task.id)]);
        this.clearMutationId(this.taskCompletingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previous);
        this.completedTasks.set(previousCompleted);
        this.taskError.set(describeGenericApiError(err, 'Failed to complete task'));
        this.clearMutationId(this.taskCompletingIds, task.id);
      }
    });
  }

  reopenTask(task: Task): void {
    if (this.taskReopeningIds()[task.id]) {
      return;
    }
    const previous = this.tasks();
    const previousCompleted = this.completedTasks();
    this.completedTasks.set(previousCompleted.filter((current) => current.id !== task.id));
    this.tasks.set([...previous, { ...task, completedAt: null }]);
    this.taskReopeningIds.update((current) => ({ ...current, [task.id]: true }));
    this.taskService.reopen(task.id).subscribe({
      next: (reopenedTask) => {
        this.tasks.update((current) => current.map((entry) => entry.id === task.id ? reopenedTask : entry));
        this.clearMutationId(this.taskReopeningIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previous);
        this.completedTasks.set(previousCompleted);
        this.taskError.set(describeGenericApiError(err, 'Failed to reopen task'));
        this.clearMutationId(this.taskReopeningIds, task.id);
      }
    });
  }

  deleteTask(task: Task): void {
    if (this.taskDeletingIds()[task.id]) {
      return;
    }
    const previous = this.tasks();
    const previousCompleted = this.completedTasks();
    this.tasks.set(previous.filter((entry) => entry.id !== task.id));
    this.completedTasks.set(previousCompleted.filter((entry) => entry.id !== task.id));
    this.taskDeletingIds.update((current) => ({ ...current, [task.id]: true }));
    this.taskService.delete(task.id).subscribe({
      next: () => {
        this.clearMutationId(this.taskDeletingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previous);
        this.completedTasks.set(previousCompleted);
        this.taskError.set(describeGenericApiError(err, 'Failed to delete task'));
        this.clearMutationId(this.taskDeletingIds, task.id);
      }
    });
  }

  onTaskDrop(event: CdkDragDrop<Task[]>): void {
    if (this.taskReorderSubmitting() || event.previousIndex === event.currentIndex) {
      return;
    }
    const reordered = [...this.taskRows()];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    const orderedIds = reordered.map((task) => task.id);
    const previous = this.tasks();
    const optimistic = reordered.map((task, nextDisplayOrder) => ({ ...task, displayOrder: nextDisplayOrder }));
    this.tasks.set(optimistic);
    this.taskError.set(null);
    this.taskReorderSubmitting.set(true);
    this.taskService.reorder(orderedIds, this.activityTypeId()).subscribe({
      next: (updatedTasks) => {
        this.tasks.set(updatedTasks);
        this.taskReorderSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.tasks.set(previous);
        this.taskError.set(describeGenericApiError(err, 'Failed to reorder tasks'));
        this.taskReorderSubmitting.set(false);
      }
    });
  }

  setNewTaskRecurrenceType(value: string): void {
    const nextType: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' = (
      value === 'DAILY' || value === 'WEEKLY' || value === 'MONTHLY'
    ) ? value : 'NONE';
    if (nextType === this.newTaskRecurrenceType()) {
      return;
    }
    this.newTaskRecurrenceType.set(nextType);
    this.newTaskRecurrenceDays.set(initialRecurrenceDays(nextType, this.dayStore.day()?.dayDate));
  }

  toggleWeeklyDay(day: number): void {
    if (this.newTaskRecurrenceType() !== 'WEEKLY') {
      return;
    }
    this.newTaskRecurrenceDays.update((current) => current.includes(day)
      ? current.filter((entry) => entry !== day)
      : [...current, day]
    );
  }

  isWeeklyDaySelected(day: number): boolean {
    return this.newTaskRecurrenceDays().includes(day);
  }

  toggleMonthlyDay(day: number): void {
    if (this.newTaskRecurrenceType() !== 'MONTHLY') {
      return;
    }
    this.newTaskRecurrenceDays.update((current) => (
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : [...current, day]
    ));
  }

  isMonthlyDaySelected(day: number): boolean {
    return this.newTaskRecurrenceType() === 'MONTHLY' && this.newTaskRecurrenceDays().includes(day);
  }

  private load(): void {
    const activityTypeId = this.activityTypeId();
    if (!activityTypeId) {
      return;
    }
    this.loading.set(true);
    this.loadError.set(null);
    const dayLoad$ = this.dayStore.hasFreshDayData()
      ? of(void 0)
      : this.dayStore.ensureLoaded();
    forkJoin({
      dayLoaded: dayLoad$,
      activityTypes: this.activityTypeService.list(),
      tasks: this.taskService.listActive(activityTypeId),
      completedTasks: this.taskService.listCompleted(activityTypeId)
    }).subscribe({
      next: ({ activityTypes, tasks, completedTasks }) => {
        const day = this.day();
        if (!day) {
          this.loadError.set(this.dayStore.dayError() ?? 'Failed to load habit detail');
          this.loading.set(false);
          return;
        }
        this.activityTypes.set(activityTypes);
        this.peerHabitIds.set(orderedHabitActivityTypeIds(activityTypes, day.habits));
        this.tasks.set(tasks);
        this.completedTasks.set(completedTasks);
        this.targetDraft.set(day.habits.find((habit) => habit.activityTypeId === activityTypeId)?.target ?? null);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        this.loadError.set(describeGenericApiError(err, 'Failed to load habit detail'));
        this.loading.set(false);
      }
    });
  }

  private buildCreateTaskRequest(title: string): CreateTaskRequest {
    const request: CreateTaskRequest = {
      activityTypeId: this.activityTypeId(),
      title
    };
    const recurrenceType = this.newTaskRecurrenceType();
    if (recurrenceType === 'NONE') {
      return request;
    }
    request.recurrenceType = recurrenceType;
    request.recurrenceInterval = 1;
    const formattedDays = formatRecurrenceDays(recurrenceType, this.newTaskRecurrenceDays());
    if (formattedDays) {
      request.recurrenceDays = formattedDays;
    }
    return request;
  }

  private applyHabitIncrement(response: HabitIncrementResponse): void {
    this.day.update((current) => {
      if (!current || current.dayDate !== response.dayDate) {
        return current;
      }
      const existingIndex = current.habits.findIndex((habit) => habit.activityTypeId === response.activityTypeId);
      if (existingIndex < 0) {
        return {
          ...current,
          habits: [...current.habits, { activityTypeId: response.activityTypeId, total: response.total, target: null, openTaskCount: this.openTaskCount() }]
        };
      }
      return {
        ...current,
        habits: current.habits.map((habit, index) => index === existingIndex
          ? { ...habit, total: response.total }
          : habit)
      };
    });
  }

  private applyHabitTarget(response: HabitTargetResponse): void {
    this.day.update((current) => {
      if (!current || current.dayDate !== response.dayDate) {
        return current;
      }
      const existingIndex = current.habits.findIndex((habit) => habit.activityTypeId === response.activityTypeId);
      if (existingIndex < 0) {
        return {
          ...current,
          habits: [...current.habits, { activityTypeId: response.activityTypeId, total: response.total, target: response.target, openTaskCount: this.openTaskCount() }]
        };
      }
      return {
        ...current,
        habits: current.habits.map((habit, index) => index === existingIndex
          ? { ...habit, total: response.total, target: response.target }
          : habit)
      };
    });
    this.targetDraft.set(response.target);
  }

  private sortByDisplayOrder(tasks: ReadonlyArray<Task>): Task[] {
    return [...tasks].sort((left, right) => {
      if (left.displayOrder !== right.displayOrder) {
        return left.displayOrder - right.displayOrder;
      }
      return left.createdAt.localeCompare(right.createdAt);
    });
  }

  private clearMutationId(
    mutationState: { update: (updater: (current: Record<string, boolean>) => Record<string, boolean>) => void },
    taskId: string
  ): void {
    mutationState.update((current) => {
      const { [taskId]: _removed, ...rest } = current;
      return rest;
    });
  }
}
